import React from 'react';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import Button from '@/components/atoms/Button';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  MagicStar,
  ShoppingBag,
  Timer,
  Verify,
  Location,
  Messages,
  Award,
  MessageText1,
  Profile2User,
  BrifecaseTimer,
} from 'iconsax-react';
import { SellerYouHireProps } from './sellerYouHireTypes';
import { Image } from 'antd';

const SellerYouHire: React.FC<SellerYouHireProps> = ({
  photo,
  name,
  companyName,
  rating,
  reviewsNumber,
  ordersNumber,
  avgResponse,
  companyBio,
  companySize,
  foundedYear,
  location,
  languages,
  joinedDate,
  totalPrice,
  deliveryTime,
  currency,
  isVerified,
}) => {
  const { t } = useTranslation();

  const displayName = companyName || name || '-';
  const effectiveCurrency = currency || t('components.SAR');

  // 1. Delivery
  const deliveryDaysMatch = typeof deliveryTime === 'string' ? deliveryTime.match(/\d+/) : null;
  const formattedDelivery = deliveryDaysMatch
    ? t('components.postDetails.deliveryDays', { count: Number(deliveryDaysMatch[0]) })
    : deliveryTime;

  // 2. Joined Date
  const formattedJoinedDate = (() => {
    if (!joinedDate) return null;
    const d = dayjs(joinedDate);
    if (d.isValid()) return t('components.joined', { date: d.format('MMM YYYY') });
    if (joinedDate.toLowerCase().includes('joined') || joinedDate.includes('انضم')) return joinedDate;
    return t('components.joined', { date: joinedDate });
  })();

  // 3. Languages
  const formattedLanguages = (() => {
    if (!languages) return null;
    if (/^languages:/i.test(languages) || languages.startsWith('اللغات:')) return languages;
    return t('components.languagesLabel', { langs: languages });
  })();

  // 4. Rating & Reviews
  const numRating = Number(rating ?? 0);
  const numReviews = Number(reviewsNumber ?? 0);
  const ratingDisplay =
    !rating || (numRating === 0 && numReviews === 0)
      ? t('components.zeroReviews')
      : `${rating}${reviewsNumber !== undefined ? ` (${reviewsNumber} ${t('components.postDetails.reviews')})` : ''}`;

  // 5. Orders
  const numOrders = Number(ordersNumber ?? 0);
  const ordersDisplay =
    !ordersNumber || numOrders === 0
      ? t('components.zeroOrder')
      : `${numOrders}+ ${t('components.postDetails.orders')}`;

  // 6. Avg Response
  const avgRespStr = avgResponse?.trim();
  const formattedAvgResponse = avgRespStr
    ? t('components.postDetails.avgResponse', {
        time: /^\d+$/.test(avgRespStr) ? `${avgRespStr}hrs` : avgRespStr.replace(/\s*response$/i, ''),
      })
    : null;

  // 7. Company Size
  const compSizeTrimmed = companySize?.trim();
  const formattedCompanySize = compSizeTrimmed
    ? /employees?/i.test(compSizeTrimmed)
      ? compSizeTrimmed.replace(/employees?/i, t('components.employees'))
      : `${compSizeTrimmed} ${t('components.employees')}`
    : null;

  return (
    <div
      className="py-8 px-4 sm:px-8 flex flex-col items-center text-center max-w-4xl mx-auto"
      data-testid="seller-you-hire-container"
    >
      {/* Centered Large Circular Avatar / Logo */}
      <div className="mb-3" data-testid="seller-avatar-header">
        {photo ? (
          <div
            className="w-[100px] h-[100px] rounded-full overflow-hidden shrink-0 flex items-center justify-center border border-light-input-border dark:border-dark-input-border [&_.ant-image]:!w-full [&_.ant-image]:!h-full [&_.ant-image-img]:!w-full [&_.ant-image-img]:!h-full [&_.ant-image-img]:!object-cover select-none"
            data-testid="seller-avatar-image"
          >
            <Image src={photo} alt={displayName} className="!w-full !h-full !object-cover" preview={false} />
          </div>
        ) : (
          <div
            className="w-[100px] h-[100px] rounded-full bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border text-light-text-primary dark:text-dark-text-primary flex items-center justify-center text-[28px] font-bold select-none"
            data-testid="seller-avatar-fallback"
          >
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Company / Seller Name */}
      <TitleText
        text={displayName}
        className="!font-bold !text-[22px] sm:!text-[24px] !leading-[120%] text-light-text-primary dark:text-dark-text-primary mb-2.5"
        data-testid="seller-company-name"
      />

      {/* Verified Supplier Badge */}
      {isVerified && (
        <div
          className="h-[34px] px-3 py-1.5 inline-flex items-center gap-1.5 rounded-[8px] bg-green-50 dark:bg-green-950/40 text-light-success dark:text-dark-success shrink-0 mb-4"
          data-testid="seller-verified-badge"
        >
          <Verify size={18} variant="Bold" className="text-light-success dark:text-dark-success" />
          <InfoText text={t('components.verifiedSupplier')} className="!text-[13px] !font-medium !leading-[100%] text-light-success dark:text-dark-success" />
        </div>
      )}

      {/* Stats Row */}
      <div
        className="flex flex-wrap items-center justify-center gap-5 sm:gap-6 text-[15px] sm:text-[16px] text-light-text-primary dark:text-dark-text-primary mb-6"
        data-testid="seller-stats-row"
      >
        {ratingDisplay && (
          <div className="flex items-center gap-1.5" data-testid="seller-rating">
            <MagicStar size={18} variant="Bold" className="text-light-alert dark:text-dark-alert" />
            <InfoText
              text={ratingDisplay}
              className="!font-normal !text-[15px] sm:!text-[16px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
            />
          </div>
        )}

        {ordersDisplay && (
          <div className="flex items-center gap-1.5" data-testid="seller-orders">
            <ShoppingBag size={18} variant="Bold" className="text-light-information dark:text-dark-information" />
            <InfoText
              text={ordersDisplay}
              className="!font-normal !text-[15px] sm:!text-[16px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
            />
          </div>
        )}

        {formattedAvgResponse && (
          <div className="flex items-center gap-1.5" data-testid="seller-avg-response">
            <Timer size={18} variant="Bold" className="text-light-success dark:text-dark-success" />
            <InfoText
              text={formattedAvgResponse}
              className="!font-normal !text-[15px] sm:!text-[16px] !leading-[100%] text-light-text-primary dark:text-dark-text-primary"
            />
          </div>
        )}
      </div>

      {/* Company Bio Section */}
      {companyBio && (
        <div
          className="flex flex-col items-center max-w-[620px] w-full mb-6"
          data-testid="seller-bio-container"
        >
          <div
            className="flex items-center gap-2 mb-2"
            data-testid="seller-bio-header"
          >
            <MessageText1
              size={18}
              variant="Bold"
              className="text-light-text-primary dark:text-dark-text-primary"
            />
            <InfoText
              text={t('components.companyBio')}
              className="!font-bold !text-[16px] !leading-[100%] !text-light-text-primary dark:!text-dark-text-primary"
              data-testid="seller-bio-title"
            />
          </div>

          <InfoText
            text={companyBio}
            maxLength="FULL"
            className="!text-[15px] sm:!text-[16px] !leading-[160%] !font-normal text-light-text-primary dark:text-dark-text-primary text-center tracking-normal break-words [overflow-wrap:anywhere]"
            data-testid="seller-bio-text"
          />
        </div>
      )}

      {/* Metadata Chips */}
      {(formattedLanguages || formattedJoinedDate || location || formattedCompanySize || foundedYear) && (
        <div
          className="flex flex-col items-center gap-3 mb-7"
          data-testid="seller-metadata-chips"
        >
          {/* Top Row Chips */}
          {(formattedLanguages || formattedJoinedDate) && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {formattedLanguages && (
                <div
                  className="flex items-center gap-2 py-2 px-3.5 bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border rounded-[8px] text-[14px] font-medium text-light-text-primary dark:text-dark-text-primary"
                  data-testid="seller-languages-chip"
                >
                  <Messages size={18} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
                  <InfoText text={formattedLanguages} className="!text-[14px] text-light-text-primary dark:text-dark-text-primary" />
                </div>
              )}

              {formattedJoinedDate && (
                <div
                  className="flex items-center gap-2 py-2 px-3.5 bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border rounded-[8px] text-[14px] font-medium text-light-text-primary dark:text-dark-text-primary"
                  data-testid="seller-joined-chip"
                >
                  <Award size={18} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
                  <InfoText text={formattedJoinedDate} className="!text-[14px] text-light-text-primary dark:text-dark-text-primary" />
                </div>
              )}
            </div>
          )}

          {/* Bottom Row Chips */}
          {(location || formattedCompanySize || foundedYear) && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              {location && (
                <div
                  className="flex items-center gap-2 py-2 px-3.5 bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border rounded-[8px] text-[14px] font-medium text-light-text-primary dark:text-dark-text-primary"
                  data-testid="seller-location-chip"
                >
                  <Location size={18} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
                  <InfoText text={location} className="!text-[14px] text-light-text-primary dark:text-dark-text-primary" />
                </div>
              )}

              {formattedCompanySize && (
                <div
                  className="flex items-center gap-2 py-2 px-3.5 bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border rounded-[8px] text-[14px] font-medium text-light-text-primary dark:text-dark-text-primary"
                  data-testid="seller-employee-count-chip"
                >
                  <Profile2User size={18} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
                  <InfoText text={formattedCompanySize} className="!text-[14px] text-light-text-primary dark:text-dark-text-primary" />
                </div>
              )}

              {foundedYear && (
                <div
                  className="flex items-center gap-2 py-2 px-3.5 bg-soft-background dark:bg-dark-surface-primary border border-light-input-border dark:border-dark-input-border rounded-[8px] text-[14px] font-medium text-light-text-primary dark:text-dark-text-primary"
                  data-testid="seller-founded-year-chip"
                >
                  <BrifecaseTimer size={18} variant="Bold" className="text-light-text-primary dark:text-dark-text-primary" />
                  <InfoText text={t('components.founded', { year: foundedYear })} className="!text-[14px] text-light-text-primary dark:text-dark-text-primary" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom Pricing & Delivery Summary Row */}
      {(totalPrice !== undefined || formattedDelivery) && (
        <div
          className="pt-6 border-light-input-border dark:border-dark-input-border w-full flex items-center justify-center gap-8 sm:gap-16 mb-7"
          data-testid="seller-pricing-delivery-summary"
        >
          {totalPrice !== undefined && (
            <div className="flex flex-col items-center" data-testid="seller-summary-total-price">
              <InfoText
                text={t('components.postDetails.totalPrice')}
                className="!text-[15px] sm:!text-[16px] !leading-[100%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal mb-2"
              />
              <div className="flex items-center gap-1.5 text-light-information dark:text-dark-information !font-bold !text-[22px] sm:!text-[24px] !leading-[100%] tracking-normal">
                <MessageText1 size={22} variant="Bold" className="text-light-information dark:text-dark-information" />
                <InfoText text={`${typeof totalPrice === 'number' ? totalPrice.toLocaleString() : totalPrice} ${effectiveCurrency}`} className="!font-bold !text-[22px] sm:!text-[24px] text-light-information dark:text-dark-information" />
              </div>
            </div>
          )}

          {totalPrice !== undefined && formattedDelivery && (
            <div className="w-[1px] h-10 bg-light-input-border dark:bg-dark-input-border" />
          )}

          {formattedDelivery && (
            <div className="flex flex-col items-center" data-testid="seller-summary-delivery-time">
              <InfoText
                text={t('components.postDetails.deliveryTime')}
                className="!text-[15px] sm:!text-[16px] !leading-[100%] !font-normal !text-light-text-primary dark:!text-dark-text-primary tracking-normal mb-2"
              />
              <div className="flex items-center gap-1.5 text-light-alert dark:text-dark-alert !font-bold !text-[22px] sm:!text-[24px] !leading-[100%] tracking-normal">
                <MessageText1 size={22} variant="Bold" className="text-light-alert dark:text-dark-alert" />
                <InfoText text={formattedDelivery} className="!font-bold !text-[22px] sm:!text-[24px] text-light-alert dark:text-dark-alert" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Track KPIs Button */}
      <Button
        variant="outline"
        className="w-full max-w-[260px] !rounded-[12px] !h-[44px] !border-light-input-border dark:!border-dark-input-border justify-center !font-semibold !text-[15px] !text-light-text-primary dark:!text-dark-text-primary shadow-xs"
        data-testid="seller-hired-track-kpis-btn"
      >
        {t('biddingPosts.trackKPIs')}
      </Button>
    </div>
  );
};

export default SellerYouHire;
